/**
 * Travel Desk — the Zod schemas the `/travel` contract is built from (ADR 0030, plan §5.3 / §5.6).
 * RN-safe: Zod and decimal.js only. Declared once so the API, the web forms and the copilot's
 * derived tools all read the same shapes (invariant 8).
 *
 * WHAT IS IN HERE. The enum schemas for every Travel Desk enum (so `travel-desk.contract.ts` and
 * `travel-policy.contract.ts` import them instead of redeclaring: two `export *` modules exporting
 * one name is a compile error), the trip's itinerary parts (legs, stays, personal days, cost split,
 * estimate lines), the trip DTOs a traveller / manager / HR reads, amendments, settlements, the
 * masked traveller profile, and the applicable-policy shape the New Trip form evaluates live.
 *
 * CONVENTIONS. Money is a decimal STRING with its currency beside it (invariant 2). Dates are
 * `YYYY-MM-DD` local calendar days (invariant 3); the trip's zone is `timezone` (the location's,
 * else the legal entity's — EC-50). Times of day are `HH:MM`, local. Instants are UTC ISO strings.
 * Every mutable row carries `version`; a stale write is a 409 (`stale_version`).
 *
 * WHAT A TRAVELLER NEVER SEES HERE. Desk-internal fields (SLA due instants, assignee, internal
 * booking notes, vendor GSTIN) are not in these DTOs; they live in `travel-desk.contract.ts`.
 * Passport and visa numbers are masked on every read; the only unmasking is the desk's audited
 * reveal route.
 */

import { Decimal } from 'decimal.js';
import { z } from 'zod';
import {
  TRAVEL_AMENDMENT_KINDS,
  TRAVEL_AMENDMENT_STATUSES,
  TRAVEL_BOOKING_KINDS,
  TRAVEL_BOOKING_MODES,
  TRAVEL_DESK_BOOKING_STATUSES,
  TRAVEL_CANCEL_REASONS,
  TRAVEL_CITY_TIERS,
  TRAVEL_CLASSES,
  TRAVEL_CLASS_LEG_MODE,
  TRAVEL_DOCUMENT_KINDS,
  TRAVEL_ENTITLEMENT_MODES,
  TRAVEL_JOURNEY_TYPES,
  TRAVEL_LEG_MODES,
  TRAVEL_POLICY_VIOLATION_SEVERITIES,
  TRAVEL_SETTLEMENT_KINDS,
  TRAVEL_SETTLEMENT_LINE_DECISIONS,
  TRAVEL_SETTLEMENT_STATUSES,
  TRAVEL_TRIP_PHASES,
  TRAVEL_TRIP_STATUSES,
  TRAVEL_TRIP_TYPES,
} from '../enums.js';
import {
  currencySchema,
  dateOnlySchema,
  decimalStringSchema,
  expenseAdvanceStatusSchema,
  instantSchema,
  orgIdSchema,
  percentSchema,
  uuidSchema,
} from '../schemas/common.js';
import { diffDays, isDateKey } from './dates.js';
import { MAX_TRAVEL_TRIP_DAYS } from './per-diem.js';
import { TRAVEL_POLICY_VIOLATION_CODES, type TravelPolicyEvaluation } from './policy.js';

// ---------------------------------------------------------------------------
// Enum schemas
// ---------------------------------------------------------------------------

export const travelTripStatusSchema = z.enum(TRAVEL_TRIP_STATUSES);
export const travelTripPhaseSchema = z.enum(TRAVEL_TRIP_PHASES);
export const travelTripTypeSchema = z.enum(TRAVEL_TRIP_TYPES);
export const travelJourneyTypeSchema = z.enum(TRAVEL_JOURNEY_TYPES);
export const travelBookingModeSchema = z.enum(TRAVEL_BOOKING_MODES);
export const travelLegModeSchema = z.enum(TRAVEL_LEG_MODES);
export const travelEntitlementModeSchema = z.enum(TRAVEL_ENTITLEMENT_MODES);
export const travelClassSchema = z.enum(TRAVEL_CLASSES);
export const travelCityTierSchema = z.enum(TRAVEL_CITY_TIERS);
export const travelPolicyViolationSeveritySchema = z.enum(TRAVEL_POLICY_VIOLATION_SEVERITIES);
export const travelPolicyViolationCodeSchema = z.enum(TRAVEL_POLICY_VIOLATION_CODES);
export const travelBookingKindSchema = z.enum(TRAVEL_BOOKING_KINDS);
export const travelDeskBookingStatusSchema = z.enum(TRAVEL_DESK_BOOKING_STATUSES);
export const travelAmendmentKindSchema = z.enum(TRAVEL_AMENDMENT_KINDS);
export const travelAmendmentStatusSchema = z.enum(TRAVEL_AMENDMENT_STATUSES);
export const travelCancelReasonSchema = z.enum(TRAVEL_CANCEL_REASONS);
export const travelSettlementKindSchema = z.enum(TRAVEL_SETTLEMENT_KINDS);
export const travelSettlementStatusSchema = z.enum(TRAVEL_SETTLEMENT_STATUSES);
export const travelSettlementLineDecisionSchema = z.enum(TRAVEL_SETTLEMENT_LINE_DECISIONS);
export const travelDocumentKindSchema = z.enum(TRAVEL_DOCUMENT_KINDS);

// ---------------------------------------------------------------------------
// Small building blocks
// ---------------------------------------------------------------------------

/** `HH:MM`, 24-hour, local to the traveller's trip timezone. */
export const travelClockTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:MM');

/** A city as the traveller typed it. Tier lookup normalises it (`normalizeCityKey`); this does not. */
const citySchema = z.string().trim().min(1).max(120);

const optionalText = (max: number) => z.string().trim().max(max).nullable().optional();

/** An amount of zero or more. Negative and malformed values are refused at the edge. */
export const travelAmountSchema = decimalStringSchema.refine(
  (value) => !value.startsWith('-'),
  'Must not be negative',
);

/** An amount strictly greater than zero — an advance of 0 is noise. */
export const travelPositiveAmountSchema = travelAmountSchema.refine(
  (value) => /[1-9]/.test(value),
  'Must be greater than zero',
);

/** The optimistic-concurrency token of a mutable row; a stale one is a 409 `stale_version`. */
export const travelVersionSchema = z.number().int().nonnegative();

const personRefSchema = z.object({ personId: uuidSchema, name: z.string() });
export type TravelPersonRef = z.infer<typeof personRefSchema>;
export const travelPersonRefSchema = personRefSchema;

// ---------------------------------------------------------------------------
// Itinerary — what a traveller (or HR on their behalf) writes
// ---------------------------------------------------------------------------

const legFields = z.object({
  mode: travelLegModeSchema,
  /** Null: no class preference. When set it must belong to `mode` (`TRAVEL_CLASS_LEG_MODE`). */
  travelClass: travelClassSchema.nullable().optional(),
  fromCity: citySchema,
  toCity: citySchema,
  /** The travel day. */
  date: dateOnlySchema,
  departureTime: travelClockTimeSchema.nullable().optional(),
  arrivalTime: travelClockTimeSchema.nullable().optional(),
  notes: optionalText(500),
});

const classMatchesMode = (leg: { mode: string; travelClass?: string | null }): boolean =>
  leg.travelClass == null ||
  TRAVEL_CLASS_LEG_MODE[leg.travelClass as keyof typeof TRAVEL_CLASS_LEG_MODE] === leg.mode;
const CLASS_MODE_MESSAGE = 'The travel class does not belong to this mode';

/**
 * One leg. `id` is sent when editing a leg that already exists so its bookings stay linked to it;
 * a leg without one is new. Legs missing from an edit are removed (an amendment, once booked).
 */
export const travelLegInputSchema = legFields
  .extend({ id: uuidSchema.optional() })
  .refine(classMatchesMode, { message: CLASS_MODE_MESSAGE, path: ['travelClass'] });
export type TravelLegInput = z.infer<typeof travelLegInputSchema>;

const stayFields = z.object({
  city: citySchema,
  /** The first night. */
  checkIn: dateOnlySchema,
  /** The morning of leaving; the night of this date is not in the stay. */
  checkOut: dateOnlySchema,
  /** The traveller's estimate per night, compared with the tier's cap. */
  nightlyRate: travelAmountSchema.nullable().optional(),
  /** Staying with friends or family: lodging in lieu instead of a hotel booking. */
  lodgingInLieu: z.boolean().default(false),
  notes: optionalText(500),
});

export const travelStayInputSchema = stayFields
  .extend({ id: uuidSchema.optional() })
  .refine((stay) => stay.checkOut > stay.checkIn, {
    message: 'Check-out must be after check-in',
    path: ['checkOut'],
  });
export type TravelStayInput = z.infer<typeof travelStayInputSchema>;

/**
 * Cost split across projects / cost centres / business units (D28, EC-7). Each row names at least
 * one dimension; the percentages of the set sum to 100 (checked on the array, below).
 */
export const travelCostAllocationSchema = z
  .object({
    projectId: uuidSchema.nullable().optional(),
    costCentreId: uuidSchema.nullable().optional(),
    businessUnitId: uuidSchema.nullable().optional(),
    percent: percentSchema,
  })
  .refine((row) => row.projectId || row.costCentreId || row.businessUnitId, {
    message: 'Name a project, a cost centre or a business unit',
  });
export type TravelCostAllocation = z.infer<typeof travelCostAllocationSchema>;

export const travelCostAllocationsSchema = z
  .array(travelCostAllocationSchema)
  .max(20)
  .refine(
    (rows) =>
      rows.length === 0 ||
      rows.reduce((sum, row) => sum.plus(row.percent), new Decimal(0)).equals(100),
    'The cost split must add up to 100%',
  );

/** One component of the trip's estimate. The trip's `estimatedAmount` is their sum. */
export const travelEstimateLineSchema = z.object({
  component: travelEntitlementModeSchema,
  amount: travelAmountSchema,
  /** How the number was reached ("Delhi–Mumbai economy, 2 people") — shown to the approver. */
  basis: optionalText(200),
});
export type TravelEstimateLine = z.infer<typeof travelEstimateLineSchema>;

/**
 * An advance asked for with the trip (plan §5.8). It is created when the trip is approved,
 * pre-filled and linked; `recoveryConsentAccepted` is the tick against the policy's
 * `recoveryConsentText` (D22) and must be `true` — the route refuses with 422 otherwise.
 */
export const travelTripAdvanceRequestSchema = z.object({
  amount: travelPositiveAmountSchema,
  recoveryConsentAccepted: z.boolean(),
});

// ---------------------------------------------------------------------------
// Trip bodies
// ---------------------------------------------------------------------------

const tripBodyFields = {
  purpose: z.string().trim().min(1).max(1000),
  /** v1 books domestic trips only; the route refuses `international` with 422 (out of scope, §1). */
  tripType: travelTripTypeSchema.default('domestic'),
  journeyType: travelJourneyTypeSchema,
  startDate: dateOnlySchema,
  endDate: dateOnlySchema,
  /** Local time the traveller leaves on `startDate` / is back on `endDate` — per-diem half days. */
  departureTime: travelClockTimeSchema.nullable().optional(),
  returnTime: travelClockTimeSchema.nullable().optional(),
  billableToClient: z.boolean().default(false),
  bookingMode: travelBookingModeSchema.default('desk'),
  /** The trip has already happened (D14). The route checks the policy's `postFactoMaxDays`. */
  isPostFacto: z.boolean().default(false),
  /** The reason a soft violation or a post-facto trip needs (D7, D14). */
  justification: optionalText(1000),
  legs: z.array(travelLegInputSchema).max(20).default([]),
  stays: z.array(travelStayInputSchema).max(20).default([]),
  /** Days on the trip that are the traveller's own: leave required, no on-duty or per diem (EC-5). */
  personalDays: z.array(dateOnlySchema).max(60).default([]),
  costAllocations: travelCostAllocationsSchema.default([]),
  estimateLines: z.array(travelEstimateLineSchema).max(40).default([]),
  advance: travelTripAdvanceRequestSchema.nullable().optional(),
};

const endNotBeforeStart = (body: { startDate?: string; endDate?: string }): boolean =>
  !body.startDate || !body.endDate || body.endDate >= body.startDate;
const END_BEFORE_START = { message: 'The end date is before the start date', path: ['endDate'] };

/**
 * Per diem and overlap checks work day by day, so the span is capped (`MAX_TRAVEL_TRIP_DAYS`).
 * `dateOnlySchema` alone lets through keys the day arithmetic rejects (years before 0100), so a key
 * that fails `isDateKey` is left to `tripDatesSupported` rather than thrown at here.
 */
const tripNotTooLong = (body: { startDate?: string; endDate?: string }): boolean =>
  !body.startDate ||
  !body.endDate ||
  !isDateKey(body.startDate) ||
  !isDateKey(body.endDate) ||
  body.endDate < body.startDate ||
  diffDays(body.startDate, body.endDate) + 1 <= MAX_TRAVEL_TRIP_DAYS;
const tripDatesSupported = (body: { startDate?: string; endDate?: string }): boolean =>
  (!body.startDate || isDateKey(body.startDate)) && (!body.endDate || isDateKey(body.endDate));
const TRIP_DATES_UNSUPPORTED = { message: 'Not a supported date', path: ['startDate'] };
const TRIP_TOO_LONG = {
  message: `A trip can be at most ${MAX_TRAVEL_TRIP_DAYS} days long`,
  path: ['endDate'],
};

/**
 * Save a new draft. A draft may be half-finished — legs, stays and estimates are all optional here
 * and the route checks completeness at SUBMIT (422 with the reasons). `personId` files the trip for
 * someone else and needs `travel:read`; `coTravellerPersonIds` creates one `invited` trip per
 * colleague in the same transaction (D5, D10, EC-9, EC-10).
 */
export const createTravelTripBodySchema = z
  .object({
    /** The traveller. Defaults to the caller. Someone else's needs `travel:read`. */
    personId: uuidSchema.optional(),
    coTravellerPersonIds: z.array(uuidSchema).max(20).default([]),
    ...tripBodyFields,
  })
  .refine(tripDatesSupported, TRIP_DATES_UNSUPPORTED)
  .refine(endNotBeforeStart, END_BEFORE_START)
  .refine(tripNotTooLong, TRIP_TOO_LONG);
export type CreateTravelTripBody = z.infer<typeof createTravelTripBodySchema>;

/**
 * Edit a DRAFT. Every field is optional; `version` is the token from the last read. A trip that is
 * already `pending` is edited by withdrawing it and starting again (EC-51) — the route refuses this
 * body on it with 409.
 */
export const updateTravelTripBodySchema = z
  .object(tripBodyFields)
  .partial()
  .extend({ version: travelVersionSchema })
  .refine(tripDatesSupported, TRIP_DATES_UNSUPPORTED)
  .refine(endNotBeforeStart, END_BEFORE_START)
  .refine(tripNotTooLong, TRIP_TOO_LONG);
export type UpdateTravelTripBody = z.infer<typeof updateTravelTripBodySchema>;

// ---------------------------------------------------------------------------
// Policy evaluation (mirrors `TravelPolicyEvaluation` in `travel/policy.ts`)
// ---------------------------------------------------------------------------

/** `TravelEntitlementRow` — one applicable row of the policy, amounts as decimal strings. */
export const travelEntitlementRowSchema = z.object({
  mode: travelEntitlementModeSchema,
  gradeRankMin: z.number().int().nullable(),
  gradeRankMax: z.number().int().nullable(),
  /** Empty: the mode has no class restriction. */
  allowedClasses: z.array(travelClassSchema),
  /** Null: the row applies to every city tier. */
  cityTier: travelCityTierSchema.nullable(),
  /** Hotel: nightly cap. Per diem / lodging in lieu: the daily amount. Null: no cap. */
  capAmount: decimalStringSchema.nullable(),
  selfBookingAllowed: z.boolean(),
  hardBlockedClasses: z.array(travelClassSchema),
});
export type TravelEntitlementRowDto = z.infer<typeof travelEntitlementRowSchema>;

export const travelPolicyViolationSchema = z.object({
  code: travelPolicyViolationCodeSchema,
  severity: travelPolicyViolationSeveritySchema,
  /** Where it applies: `startDate`, `legs[1].travelClass`, `stays[0].nightlyRate`… */
  field: z.string(),
  limit: z.string().optional(),
  actual: z.string().optional(),
  message: z.string(),
});
export type TravelPolicyViolationDto = z.infer<typeof travelPolicyViolationSchema>;

export const travelPolicyWarningSchema = z.object({
  code: z.enum(['overlap_leave', 'overlap_trip', 'overlap_on_duty']),
  /** The leave request / trip / on-duty row the trip overlaps. */
  refId: z.string(),
  dates: z.array(dateOnlySchema),
  message: z.string(),
});
export type TravelPolicyWarningDto = z.infer<typeof travelPolicyWarningSchema>;

const perDiemDayLineSchema = z.object({
  date: dateOnlySchema,
  cityTier: travelCityTierSchema,
  dayKind: z.enum(['single', 'first', 'middle', 'last']),
  halfDay: z.boolean(),
  /** Percentage of the rate paid, before meals. */
  pct: z.string(),
  rate: decimalStringSchema,
  /** True when the policy has no per-diem rate for this tier. */
  rateMissing: z.boolean(),
  allowance: decimalStringSchema,
  mealDeduction: decimalStringSchema,
  lodgingInLieu: decimalStringSchema,
  amount: decimalStringSchema,
});

export const travelPerDiemResultSchema = z.object({
  currency: z.string(),
  days: z.array(perDiemDayLineSchema),
  /** Personal days: no line, nothing paid. */
  excludedDates: z.array(dateOnlySchema),
  allowanceTotal: decimalStringSchema,
  mealDeductionTotal: decimalStringSchema,
  lodgingInLieuTotal: decimalStringSchema,
  total: decimalStringSchema,
});
export type TravelPerDiemResultDto = z.infer<typeof travelPerDiemResultSchema>;

/** The whole evaluation. `currency` fields are plain strings, as in the pure function's result. */
export const travelPolicyEvaluationSchema = z.object({
  entitlements: z.object({
    gradeRankUsed: z.number().int().nullable(),
    rows: z.array(travelEntitlementRowSchema),
    selfBookingAllowed: z.boolean(),
  }),
  violations: z.array(travelPolicyViolationSchema),
  warnings: z.array(travelPolicyWarningSchema),
  /** A soft violation or a post-facto trip needs the traveller's reason. */
  requiresJustification: z.boolean(),
  /** Null when the policy pays no per diem. */
  estimatedPerDiem: travelPerDiemResultSchema.nullable(),
  stayLimits: z.array(
    z.object({
      index: z.number().int().nonnegative(),
      cityTier: travelCityTierSchema,
      nightlyCap: decimalStringSchema.nullable(),
    }),
  ),
  /** EC-49: no grade on the start date, the lowest band was used. HR sees this as a data warning. */
  gradeMissing: z.boolean(),
});
export type TravelPolicyEvaluationDto = z.infer<typeof travelPolicyEvaluationSchema>;

type AssertExtends<T extends U, U> = T;
/**
 * Compile-time tripwire: the wire shape must stay assignable to the pure function's result, so the
 * two cannot drift apart silently. (The reverse needs a cast for `readonly` arrays, by design.)
 */
export type TravelPolicyEvaluationConforms = AssertExtends<
  TravelPolicyEvaluationDto,
  TravelPolicyEvaluation
>;

/**
 * The evaluation stored on the trip at submit (D7): the server's own run, against the policy in
 * force on the trip's start date (D12). `hardBlockOverridden` is true once a `travel:approve`
 * holder has waived the hard blocks (D24, EC-53).
 */
export const travelTripPolicyResultSchema = z.object({
  evaluatedAt: instantSchema,
  /** Null when the organisation has no travel policy configured. */
  policyId: uuidSchema.nullable(),
  evaluation: travelPolicyEvaluationSchema,
  hardBlockOverridden: z.boolean(),
});
export type TravelTripPolicyResult = z.infer<typeof travelTripPolicyResultSchema>;

// ---------------------------------------------------------------------------
// The applicable policy (input to the live check in the New Trip form)
// ---------------------------------------------------------------------------

/** `PerDiemRule` — percentages are 0–100 decimal strings. */
export const travelPerDiemRuleSnapshotSchema = z.object({
  halfDayBelowHours: z.string().nullable(),
  firstDayPct: percentSchema,
  lastDayPct: percentSchema,
  breakfastPct: percentSchema,
  lunchPct: percentSchema,
  dinnerPct: percentSchema,
});

/**
 * `TravelPolicySnapshot` on the wire: exactly what `evaluateTravelPolicy` takes, so the form runs
 * the same function the server does. `cityTiers` lists the cities of the trip's country that are
 * tier 1 or 2, keyed by `normalizeCityKey`; a city not listed is tier 3 (D26).
 */
export const travelPolicySnapshotSchema = z.object({
  currency: z.string(),
  leadTimeDays: z.number().int().nonnegative(),
  postFactoMaxDays: z.number().int().nonnegative(),
  maxAdvancePct: percentSchema.nullable(),
  maxAdvanceAmount: decimalStringSchema.nullable(),
  entitlements: z.array(travelEntitlementRowSchema),
  perDiemRule: travelPerDiemRuleSnapshotSchema.nullable(),
  cityTiers: z.record(z.string(), travelCityTierSchema),
});
export type TravelPolicySnapshotDto = z.infer<typeof travelPolicySnapshotSchema>;

export const travelApplicablePolicyQuerySchema = z.object({
  /** Defaults to the caller. Someone else's needs `travel:read`. */
  personId: uuidSchema.optional(),
  /** The policy and the traveller's grade are read as of this day (D12). */
  startDate: dateOnlySchema,
});

export const travelApplicablePolicySchema = z.object({
  personId: uuidSchema,
  /** Null when no travel policy applies: the form shows no limits and the server evaluates none. */
  policy: z
    .object({
      policyId: uuidSchema,
      validFrom: dateOnlySchema,
      validTo: dateOnlySchema.nullable(),
      legalEntityId: uuidSchema.nullable(),
      snapshot: travelPolicySnapshotSchema,
      /** Days after the trip within which the main settlement is due (drives the advance's date). */
      settleWithinDays: z.number().int().nonnegative(),
      supplementaryWindowDays: z.number().int().nonnegative(),
      /** What the traveller ticks when asking for an advance (D22). */
      recoveryConsentText: z.string().nullable(),
    })
    .nullable(),
  gradeRank: z.number().int().nullable(),
  /** EC-49. */
  gradeMissing: z.boolean(),
  /** The traveller's legal-entity currency — trip money is in it (EC-6). */
  currency: currencySchema,
  /** IANA zone the trip's dates are read in (EC-50). */
  timezone: z.string(),
});
export type TravelApplicablePolicy = z.infer<typeof travelApplicablePolicySchema>;

// ---------------------------------------------------------------------------
// Trip DTOs
// ---------------------------------------------------------------------------

/** One leg as stored: the input plus its identity, order, and what the policy said about it. */
export const travelLegSchema = legFields.extend({
  id: uuidSchema,
  /** 1-based order on the itinerary. */
  sequence: z.number().int().positive(),
});
export type TravelLeg = z.infer<typeof travelLegSchema>;

export const travelStaySchema = stayFields.extend({
  id: uuidSchema,
  /** Resolved from the city when saved (D26), and the cap that applied (null: none). */
  cityTier: travelCityTierSchema,
  nightlyCap: decimalStringSchema.nullable(),
});
export type TravelStay = z.infer<typeof travelStaySchema>;

export const travelPersonalDaySchema = z.object({
  date: dateOnlySchema,
  /** The leave request that covers the day; null while none is filed (submit refuses without leave, EC-5). */
  leaveRequestId: uuidSchema.nullable(),
});

/** One traveller-facing option the desk shared for a booking. */
export const tripBookingOptionSchema = z.object({
  id: uuidSchema,
  summary: z.string(),
  amount: decimalStringSchema,
  currency: currencySchema,
  withinPolicy: z.boolean(),
  expiresAt: instantSchema,
  selectedAt: instantSchema.nullable(),
});
export type TripBookingOption = z.infer<typeof tripBookingOptionSchema>;

/** A file the desk attached to the trip (ticket, voucher, invoice). Bytes: `downloadTripDocument`. */
export const tripDocumentSchema = z.object({
  id: uuidSchema,
  bookingId: uuidSchema.nullable(),
  kind: travelDocumentKindSchema,
  fileName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().int().nonnegative(),
  uploadedAt: instantSchema,
});
export type TripDocument = z.infer<typeof tripDocumentSchema>;

/**
 * A booking as the traveller and manager see it. The desk's own view (assignee, SLA, GSTIN,
 * internal trail) is `travel-desk.contract.ts`. `amount` is null until the desk confirms.
 */
export const tripBookingSchema = z.object({
  id: uuidSchema,
  kind: travelBookingKindSchema,
  legId: uuidSchema.nullable(),
  stayId: uuidSchema.nullable(),
  status: travelDeskBookingStatusSchema,
  vendorName: z.string().nullable(),
  bookingRef: z.string().nullable(),
  amount: decimalStringSchema.nullable(),
  currency: currencySchema,
  bookedAt: instantSchema.nullable(),
  /** The traveller's choices, present while `status` is `options_shared`. */
  options: z.array(tripBookingOptionSchema),
  /** Why the desk could not book it (`declined`), shown so the traveller can amend or self-book. */
  declineReason: z.string().nullable(),
  version: travelVersionSchema,
});
export type TripBooking = z.infer<typeof tripBookingSchema>;

/** The advance linked to the trip, for the money panel (plan §5.8). */
export const tripAdvanceSummarySchema = z.object({
  id: uuidSchema,
  reference: z.string(),
  status: expenseAdvanceStatusSchema,
  amount: decimalStringSchema,
  outstandingAmount: decimalStringSchema,
  currency: currencySchema,
});
export type TripAdvanceSummary = z.infer<typeof tripAdvanceSummarySchema>;

/** A co-traveller's trip in the same group (D5). Each has their own status and approver. */
export const tripGroupMemberSchema = z.object({
  tripId: uuidSchema,
  /** Null while that co-traveller's trip is still a draft or an invitation (see `travelTripSchema`). */
  reference: z.string().nullable(),
  personId: uuidSchema,
  personName: z.string(),
  status: travelTripStatusSchema,
});

/** A `travel:approve` action recorded on the trip (D24, EC-53) — visible to everyone who sees it. */
export const travelOverrideKindSchema = z.enum(['reopen', 'cancel', 'allow_hard_block']);
export const travelTripOverrideSchema = z.object({
  kind: travelOverrideKindSchema,
  byPersonId: uuidSchema,
  byName: z.string(),
  reason: z.string(),
  at: instantSchema,
});

/** A row in a trip list. */
export const travelTripSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  /**
   * e.g. TRV-2026-00012 — allocated on submit (plan TD-2.4), so null on a draft and on an invited
   * trip. Allocating at creation would burn numbers on every abandoned draft and invitation.
   */
  reference: z.string().nullable(),
  personId: uuidSchema,
  personName: z.string(),
  requestedById: uuidSchema,
  requestedByName: z.string(),
  /** Set on an `invited` trip: the colleague who added this person (D10). */
  invitedById: uuidSchema.nullable(),
  invitedByName: z.string().nullable(),
  groupId: uuidSchema.nullable(),
  legalEntityId: uuidSchema,
  locationId: uuidSchema.nullable(),
  /** Snapshotted from the assignment on the start date (D12); null when the person had none. */
  gradeRank: z.number().int().nullable(),
  purpose: z.string(),
  tripType: travelTripTypeSchema,
  journeyType: travelJourneyTypeSchema,
  startDate: dateOnlySchema,
  endDate: dateOnlySchema,
  /** Derived from the dates in the trip's timezone, never stored (plan §5.2). */
  phase: travelTripPhaseSchema,
  /** The places the itinerary visits, in order, for a one-line summary. */
  destinations: z.array(z.string()),
  status: travelTripStatusSchema,
  bookingMode: travelBookingModeSchema,
  isPostFacto: z.boolean(),
  billableToClient: z.boolean(),
  estimatedAmount: decimalStringSchema,
  currency: currencySchema,
  /** True when the stored evaluation has any violation (the approver's badge). */
  hasPolicyViolation: z.boolean(),
  workflowInstanceId: uuidSchema.nullable(),
  version: travelVersionSchema,
  submittedAt: instantSchema.nullable(),
  bookedAt: instantSchema.nullable(),
  closedAt: instantSchema.nullable(),
  cancelledAt: instantSchema.nullable(),
  cancelReason: travelCancelReasonSchema.nullable(),
  createdAt: instantSchema,
});
export type TravelTrip = z.infer<typeof travelTripSchema>;

/** One trip, everything a screen needs. */
export const travelTripDetailSchema = travelTripSchema.extend({
  /** The zone the dates are read in: the location's, else the legal entity's (EC-50). */
  timezone: z.string(),
  departureTime: travelClockTimeSchema.nullable(),
  returnTime: travelClockTimeSchema.nullable(),
  justification: z.string().nullable(),
  legs: z.array(travelLegSchema),
  stays: z.array(travelStaySchema),
  personalDays: z.array(travelPersonalDaySchema),
  costAllocations: z.array(travelCostAllocationSchema),
  estimateLines: z.array(travelEstimateLineSchema),
  /** Null on a draft that has never been evaluated on the server. */
  policyResult: travelTripPolicyResultSchema.nullable(),
  bookings: z.array(tripBookingSchema),
  documents: z.array(tripDocumentSchema),
  advance: tripAdvanceSummarySchema.nullable(),
  groupMembers: z.array(tripGroupMemberSchema),
  /** Who the approval is waiting on, while `pending`. */
  pendingApprovers: z.array(personRefSchema),
  /** The latest `travel:approve` action, or null. */
  override: travelTripOverrideSchema.nullable(),
  /** `noMoreExpensesAt`: the traveller said they have nothing more to claim (D13). */
  noMoreExpensesAt: instantSchema.nullable(),
  /** When the expense window ends and the trip auto-closes; null until it starts. */
  closeAfter: instantSchema.nullable(),
});
export type TravelTripDetail = z.infer<typeof travelTripDetailSchema>;

/**
 * One line of a trip's history. What the TRAVELLER may see: desk-internal events (assignment, SLA
 * breach, internal comments) are left out; the desk's own trail is on the booking.
 * `question_asked` / `question_answered` arrive with the workflow's ask-a-question (WF-1, D20).
 */
export const TRAVEL_TIMELINE_EVENT_KINDS = [
  'created',
  'invited',
  'invite_accepted',
  'invite_declined',
  'edited',
  'submitted',
  'approved',
  'rejected',
  'withdrawn',
  'question_asked',
  'question_answered',
  'booking_options_shared',
  'booking_confirmed',
  'booking_declined',
  'booked',
  'needs_action',
  'amendment_raised',
  'amendment_approved',
  'amendment_rejected',
  'cancelled',
  'expired',
  'settlement_opened',
  'settlement_submitted',
  'settlement_decided',
  'no_more_expenses',
  'override',
  'closed',
] as const;
export const travelTimelineEventKindSchema = z.enum(TRAVEL_TIMELINE_EVENT_KINDS);
export type TravelTimelineEventKind = z.infer<typeof travelTimelineEventKindSchema>;

export const travelTimelineEventSchema = z.object({
  id: uuidSchema,
  kind: travelTimelineEventKindSchema,
  at: instantSchema,
  /** Null for something the system did (a job, a sweep). */
  actorPersonId: uuidSchema.nullable(),
  actorName: z.string().nullable(),
  /** A short sentence, already free of amounts and PII beyond names. */
  summary: z.string(),
  note: z.string().nullable(),
});
export type TravelTimelineEvent = z.infer<typeof travelTimelineEventSchema>;

// ---------------------------------------------------------------------------
// List queries
// ---------------------------------------------------------------------------

/** A query-string boolean: `?isPostFacto=true`. (`z.coerce.boolean()` would read "false" as true.) */
const queryBooleanSchema = z
  .enum(['true', 'false'])
  .optional()
  .transform((value) => (value === undefined ? undefined : value === 'true'));

/** "My trips": the caller's own, invites included, newest first. */
export const listMyTravelTripsQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
  status: travelTripStatusSchema.optional(),
});

/**
 * Every trip in the caller's `travel:read` scope. `from`/`to` keep trips that touch that range
 * (inclusive). `q` matches the reference, the purpose and the traveller's name.
 */
export const listAllTravelTripsQuerySchema = listMyTravelTripsQuerySchema.extend({
  personId: uuidSchema.optional(),
  legalEntityId: uuidSchema.optional(),
  from: dateOnlySchema.optional(),
  to: dateOnlySchema.optional(),
  isPostFacto: queryBooleanSchema,
  hasPolicyViolation: queryBooleanSchema,
  q: z.string().trim().min(1).max(120).optional(),
});

// ---------------------------------------------------------------------------
// Who is travelling
// ---------------------------------------------------------------------------

/** `from`/`to` are required so the answer is never "everything"; the route caps the span (422). */
export const travellingQuerySchema = z.object({
  from: dateOnlySchema,
  to: dateOnlySchema,
  legalEntityId: uuidSchema.optional(),
  locationId: uuidSchema.optional(),
  /** A destination city, matched as `normalizeCityKey` does. */
  city: z.string().trim().min(1).max(120).optional(),
  q: z.string().trim().min(1).max(120).optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});

/** Approved-and-live trips only (`approved`, `needs_action`, `booked`, `settling`). No money. */
export const travellingRowSchema = z.object({
  tripId: uuidSchema,
  reference: z.string(),
  personId: uuidSchema,
  personName: z.string(),
  departmentName: z.string().nullable(),
  locationName: z.string().nullable(),
  startDate: dateOnlySchema,
  endDate: dateOnlySchema,
  phase: travelTripPhaseSchema,
  status: travelTripStatusSchema,
  destinations: z.array(z.string()),
});
export type TravellingRow = z.infer<typeof travellingRowSchema>;

/** A traveller's emergency contact, for the people who look after travellers (`travel:read`). */
export const travelEmergencyContactSchema = z.object({
  personId: uuidSchema,
  name: z.string().nullable(),
  relationship: z.string().nullable(),
  phone: z.string().nullable(),
});

// ---------------------------------------------------------------------------
// Decisions and lifecycle bodies
// ---------------------------------------------------------------------------

/** Approve: a comment is optional. */
export const travelApproveBodySchema = z.object({
  comment: z.string().trim().max(1000).optional(),
});

/** Reject: the traveller is owed a reason. */
export const travelRejectBodySchema = z.object({
  comment: z.string().trim().min(1).max(1000),
});

export const submitTravelTripBodySchema = z.object({
  version: travelVersionSchema,
  /** Adds to or replaces the draft's `justification`; required when the evaluation says so. */
  justification: optionalText(1000),
});

export const withdrawTravelBodySchema = z.object({
  /** The token of the row being withdrawn (trip or settlement). Optional for a settlement. */
  version: travelVersionSchema.optional(),
  reason: z.string().trim().max(500).optional(),
});

export const acceptTravelInviteBodySchema = z.object({
  version: travelVersionSchema,
});

export const declineTravelInviteBodySchema = z.object({
  version: travelVersionSchema.optional(),
  reason: z.string().trim().max(500).optional(),
});

/** D13: nothing more to claim — closes the trip when nothing is outstanding. */
export const noMoreExpensesBodySchema = z.object({
  version: travelVersionSchema,
});

// ---------------------------------------------------------------------------
// Override actions (`travel:approve`, D24, EC-53)
// ---------------------------------------------------------------------------

/** Every override needs a reason; it is audited and shown on the trip. */
export const travelOverrideBodySchema = z.object({
  version: travelVersionSchema,
  reason: z.string().trim().min(1).max(1000),
});

export const travelForceCancelBodySchema = travelOverrideBodySchema.extend({
  cancelReason: travelCancelReasonSchema.default('business'),
});

// ---------------------------------------------------------------------------
// Amendments
// ---------------------------------------------------------------------------

/**
 * What a change proposes: only the parts that change. `cancel` proposes nothing; `cut_short` and
 * `extend` propose `endDate` (and usually the leg and stay that move with it).
 */
export const travelAmendmentProposedSchema = z.object({
  startDate: dateOnlySchema.optional(),
  endDate: dateOnlySchema.optional(),
  departureTime: travelClockTimeSchema.nullable().optional(),
  returnTime: travelClockTimeSchema.nullable().optional(),
  legs: z.array(travelLegInputSchema).max(20).optional(),
  stays: z.array(travelStayInputSchema).max(20).optional(),
  personalDays: z.array(dateOnlySchema).max(60).optional(),
  estimateLines: z.array(travelEstimateLineSchema).max(40).optional(),
});
export type TravelAmendmentProposed = z.infer<typeof travelAmendmentProposedSchema>;

/**
 * `desk_overspend` is raised by the system (D11) and cannot be filed by a caller. The route
 * decides whether the change needs approval: cost up by more than the tolerance, or a date / route
 * change; anything else is created already `approved`. The old itinerary stays valid until then.
 */
export const RAISABLE_TRAVEL_AMENDMENT_KINDS = [
  'change',
  'add_leg',
  'remove_leg',
  'cancel',
  'cut_short',
  'extend',
] as const;
export const raisableTravelAmendmentKindSchema = z.enum(RAISABLE_TRAVEL_AMENDMENT_KINDS);

export const raiseTravelAmendmentBodySchema = z
  .object({
    /** The trip's token: the change is against this version of the itinerary. */
    version: travelVersionSchema,
    kind: raisableTravelAmendmentKindSchema,
    reason: z.string().trim().min(1).max(1000),
    /** Required for `cancel`: decides who bears the charge by default (D27). */
    cancelReason: travelCancelReasonSchema.optional(),
    proposed: travelAmendmentProposedSchema.default({}),
  })
  .superRefine((body, ctx) => {
    if (body.kind === 'cancel' && !body.cancelReason) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['cancelReason'],
        message: 'A cancellation needs a reason: business or personal',
      });
    }
    // Both dates given: the span is checked here. With only one, the route checks it against the
    // trip's stored date.
    if (!tripDatesSupported(body.proposed)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['proposed', 'startDate'],
        message: TRIP_DATES_UNSUPPORTED.message,
      });
    } else if (!tripNotTooLong(body.proposed)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['proposed', 'endDate'],
        message: TRIP_TOO_LONG.message,
      });
    }
    if ((body.kind === 'cut_short' || body.kind === 'extend') && !body.proposed.endDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['proposed', 'endDate'],
        message: 'Give the new end date',
      });
    }
  });
export type RaiseTravelAmendmentBody = z.infer<typeof raiseTravelAmendmentBodySchema>;

export const travelAmendmentSchema = z.object({
  id: uuidSchema,
  tripId: uuidSchema,
  tripReference: z.string(),
  kind: travelAmendmentKindSchema,
  status: travelAmendmentStatusSchema,
  reason: z.string(),
  cancelReason: travelCancelReasonSchema.nullable(),
  proposed: travelAmendmentProposedSchema,
  /** Signed: new estimate minus the approved one, in the trip's currency. */
  costDelta: decimalStringSchema,
  currency: currencySchema,
  /** False: created already `approved` (a small change within tolerance). */
  needsApproval: z.boolean(),
  workflowInstanceId: uuidSchema.nullable(),
  raisedBy: z.enum(['traveller', 'desk', 'system']),
  raisedById: uuidSchema.nullable(),
  raisedByName: z.string().nullable(),
  createdAt: instantSchema,
  decidedAt: instantSchema.nullable(),
});
export type TravelAmendment = z.infer<typeof travelAmendmentSchema>;

// ---------------------------------------------------------------------------
// Option select
// ---------------------------------------------------------------------------

/** Choose one of the options the desk shared. `version` is the booking's token (EC-54). */
export const selectTravelOptionBodySchema = z.object({
  version: travelVersionSchema,
});

/**
 * How a traveller settles an item the desk could not book (`declined`) or whose options all lapsed:
 * `drop` (no longer needed) or `self_book` (they book it themselves and claim it; only where the
 * policy lets their grade self-book that mode, D1). `version` is the booking's token (EC-54). The
 * third way, amending the trip, is an amendment (`raiseTravelAmendmentBodySchema`).
 */
export const TRAVEL_ITEM_RESOLUTIONS = ['drop', 'self_book'] as const;
export const travelItemResolutionSchema = z.enum(TRAVEL_ITEM_RESOLUTIONS);
export type TravelItemResolution = z.infer<typeof travelItemResolutionSchema>;

export const resolveTravelItemBodySchema = z.object({
  version: travelVersionSchema,
  action: travelItemResolutionSchema,
});

/** An approver's decision on a pending amendment. Rejecting owes the traveller a reason. */
export const decideTravelAmendmentBodySchema = z
  .object({
    decision: z.enum(['approve', 'reject']),
    comment: z.string().trim().max(1000).optional(),
  })
  .refine((body) => body.decision === 'approve' || (body.comment ?? '').length > 0, {
    message: 'A rejection needs a reason',
    path: ['comment'],
  });

/**
 * Cancel the whole trip. The traveller can only say business or personal (decides who bears the
 * charge by default, D27); `exit` is set by the system when a person leaves. It is a `cancel`
 * amendment under the hood, so it may need approval first.
 */
export const travelTravellerCancelReasonSchema = z.enum(['business', 'personal']);

export const cancelTravelTripBodySchema = z.object({
  /** The trip's token. */
  version: travelVersionSchema,
  cancelReason: travelTravellerCancelReasonSchema,
  reason: z.string().trim().min(1).max(1000),
});

// ---------------------------------------------------------------------------
// What the approver (and the traveller answering them) sees beside a trip
// ---------------------------------------------------------------------------

/**
 * `TravelApprovalContext` (`apps/api/src/lib/travel/trip/approval-context.ts`) on the wire. Sent as
 * `approval` beside a trip on every route that returns one. `violations` are the SOFT rules the
 * submitted trip breaks (a hard one blocks the submit); codes are plain strings because they come
 * out of the stored evaluation JSON. The thread is the CURRENT question/answer exchange: once the
 * approver decides, their own comment replaces it.
 */
export const travelApprovalViolationSchema = z.object({
  code: z.string(),
  severity: z.string(),
  field: z.string().nullable(),
  limit: z.string().nullable(),
  actual: z.string().nullable(),
  message: z.string(),
});

export const travelApprovalThreadEntrySchema = z.object({
  kind: z.enum(['question', 'answer']),
  text: z.string(),
  byPersonId: uuidSchema.nullable(),
  byName: z.string().nullable(),
  /** When the question was asked; answers are not timestamped by the engine, so null. */
  at: instantSchema.nullable(),
});

export const travelApprovalStepSchema = z.object({
  stepNo: z.number().int(),
  approverPersonId: uuidSchema,
  approverName: z.string().nullable(),
  dueAt: instantSchema.nullable(),
  /** `answer`: the approver asked and the traveller has not replied (the SLA is paused). */
  awaiting: z.enum(['decision', 'answer']),
});

export const travelApprovalContextSchema = z.object({
  violations: z.array(travelApprovalViolationSchema),
  violationCodes: z.array(z.string()),
  requiresJustification: z.boolean(),
  currentStep: travelApprovalStepSchema.nullable(),
  thread: z.array(travelApprovalThreadEntrySchema),
});
export type TravelApprovalContextDto = z.infer<typeof travelApprovalContextSchema>;

// ---------------------------------------------------------------------------
// The group, as a co-traveller sees it
// ---------------------------------------------------------------------------

/**
 * One member of the trip's group, for the people travelling together: names and progress only,
 * never money, references, documents or policy. `bookings` is the member's live bookings by kind
 * (a shared cab shows on each member it covers, `shared: true`).
 */
export const travelGroupRosterMemberSchema = z.object({
  personId: uuidSchema,
  personName: z.string(),
  isYou: z.boolean(),
  /** That member's own trip status (`invited` until they accept). */
  status: travelTripStatusSchema,
  bookings: z.array(
    z.object({
      kind: travelBookingKindSchema,
      /** "Flight, 12 Oct" or "Hotel, 2 nights": a label the route builds, no amounts. */
      summary: z.string(),
      status: travelDeskBookingStatusSchema,
      shared: z.boolean(),
    }),
  ),
});
export type TravelGroupRosterMember = z.infer<typeof travelGroupRosterMemberSchema>;

export const travelGroupRosterSchema = z.object({
  /** Null for a trip with no co-travellers: `members` is then just the caller. */
  groupId: uuidSchema.nullable(),
  members: z.array(travelGroupRosterMemberSchema),
});
export type TravelGroupRoster = z.infer<typeof travelGroupRosterSchema>;

// Settlements: the round, line, netting and recovery shapes live in `travel/money-schemas.ts`
// (Phase 4), next to the pure money logic they mirror.

// ---------------------------------------------------------------------------
// Traveller profile
// ---------------------------------------------------------------------------
//
/** One frequent-flyer or loyalty membership: the programme's airline or brand, and the number. */
export const travelFrequentFlyerSchema = z.object({
  airline: z.string().trim().min(1).max(80),
  number: z.string().trim().min(1).max(40),
});
export type TravelFrequentFlyer = z.infer<typeof travelFrequentFlyerSchema>;

// There is ONE profile shape and it lives in `contracts/travel-request.contract.ts`
// (`travellerProfileViewSchema`, `updateSelfTravellerProfileBodySchema`): the routes, the web and
// the desk reveal (`travelDeskTravellerRevealSchema`) all use it. It is not declared here so it
// cannot drift.
